/**
 * Internal hold controls for the review browser (GDN-4065). Presentational: the
 * host persists holds and hands back the server's per-row result. Words live in
 * `internal-hold-copy.ts`.
 */
import { useEffect, useState } from 'react';
import PauseCircleOutlinedIcon from '@mui/icons-material/PauseCircleOutlined';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import {
  commitHeldHeading,
  commitHeldMore,
  HOLD_REASON_MAX,
  HOLD_REFUSED_FLAGGED,
  heldRowMessage,
  holdResultSummary,
} from './internal-hold-copy';
import type {
  CommitHeldResult,
  CommitHeldRow,
  HoldActionResult,
  HoldFilterMode,
  InternalHold,
} from './internal-hold-copy';

/**
 * Ask for the required reason before placing a hold. `count` is how many rows
 * the hold is aimed at; the server may still refuse some, and says which.
 * With `currentReason` it edits a held row's reason instead: re-holding with a
 * new reason is how the server changes one (outcome `reason_updated`).
 */
export function HoldReasonDialog({
  open,
  count,
  currentReason,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  count: number;
  currentReason?: string;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}) {
  const editing = currentReason !== undefined;
  const [reason, setReason] = useState('');
  // Start from the current reason each time the dialog opens for an edit.
  useEffect(() => {
    if (open) setReason(currentReason ?? '');
  }, [open, currentReason]);
  const trimmed = reason.trim();
  // The same reason changes nothing server-side, so it is not a save.
  const unchanged = editing && trimmed === currentReason.trim();
  // Counted, not capped: a hard cap would silently cut a pasted reason short.
  const tooLong = trimmed.length > HOLD_REASON_MAX;
  const close = () => {
    setReason('');
    onCancel();
  };
  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
      <DialogTitle>
        {editing
          ? 'Change the hold reason'
          : count === 1
            ? 'Hold this row internally'
            : `Hold ${count.toLocaleString()} selected rows internally`}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          A held row stays out of every commit until someone releases it. The practice is not
          notified and never sees the reason.
        </Typography>
        <TextField
          autoFocus
          required
          fullWidth
          multiline
          minRows={2}
          label="Reason"
          value={reason}
          onChange={e => setReason(e.target.value)}
          error={tooLong}
          helperText={
            tooLong
              ? `${trimmed.length.toLocaleString()} of ${HOLD_REASON_MAX} characters. Shorten the reason to save it.`
              : 'Required. Say what the row is waiting on: an engineering fix, a dependency, a review. No clinical detail.'
          }
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Cancel</Button>
        <Button
          variant="contained"
          disabled={trimmed === '' || unchanged || tooLong}
          onClick={() => {
            setReason('');
            onConfirm(trimmed);
          }}
        >
          {editing ? 'Save reason' : 'Hold'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * The row header's hold control. A held row shows the hold and "Release hold";
 * a flagged row shows "Hold internally" DISABLED, never hidden — the reason
 * sits beside it in `RowGateNote`.
 */
export function RowHoldAction({
  hold,
  flagged,
  onHold,
  onRelease,
  onEditReason,
  disabled,
}: {
  hold: InternalHold | undefined;
  flagged: boolean;
  onHold: () => void;
  onRelease: () => void;
  onEditReason?: () => void;
  disabled?: boolean;
}) {
  if (hold) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Chip
          icon={<PauseCircleOutlinedIcon fontSize="small" />}
          label="Held internally"
          size="small"
          color="info"
          variant="filled"
        />
        <Button
          size="small"
          color="inherit"
          disabled={disabled}
          onClick={e => {
            e.stopPropagation();
            onRelease();
          }}
        >
          Release hold
        </Button>
        {onEditReason && (
          <Button
            size="small"
            color="inherit"
            disabled={disabled}
            onClick={e => {
              e.stopPropagation();
              onEditReason();
            }}
          >
            Edit reason
          </Button>
        )}
      </Box>
    );
  }
  return (
    <Button
      size="small"
      variant="outlined"
      color="info"
      startIcon={<PauseCircleOutlinedIcon fontSize="small" />}
      disabled={flagged || disabled}
      onClick={e => {
        e.stopPropagation();
        onHold();
      }}
    >
      Hold internally
    </Button>
  );
}

/**
 * The sentence that explains a disabled action, on the row itself and visible
 * without hovering or expanding (AC 9). A flagged row can't be held; a held row
 * can't be flagged, resolved or committed.
 */
export function RowGateNote({
  hold,
  flagged,
}: {
  hold: InternalHold | undefined;
  flagged: boolean;
}) {
  const text = hold ? heldRowMessage(hold) : flagged ? HOLD_REFUSED_FLAGGED : null;
  if (!text) return null;
  return (
    <Typography
      variant="caption"
      color="text.secondary"
      component="p"
      sx={{ px: 1, pb: 1, mt: -0.5, pl: 6 }}
    >
      {text}
    </Typography>
  );
}

/** Held count and the All / Held / Not held filter. */
export function HeldBar({
  total,
  notHeldTotal,
  filterMode = 'all',
  onFilterModeChange,
}: {
  total: number;
  notHeldTotal?: number;
  filterMode?: HoldFilterMode;
  onFilterModeChange?: (mode: HoldFilterMode) => void;
}) {
  const filterUseful = !!onFilterModeChange && (total > 0 || (notHeldTotal ?? 0) > 0);
  if (total === 0 && !filterUseful) return null;
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1.5 }}>
      {total > 0 && (
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {total.toLocaleString()} held internally
        </Typography>
      )}
      {filterUseful && (
        <ToggleButtonGroup
          size="small"
          exclusive
          value={filterMode}
          // MUI passes null when the active segment is re-clicked; keep one on.
          onChange={(_, mode: HoldFilterMode | null) => {
            if (mode) onFilterModeChange?.(mode);
          }}
          aria-label="Filter by internal hold"
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="held">
            <PauseCircleOutlinedIcon fontSize="small" sx={{ mr: 0.5 }} />
            Held {total.toLocaleString()}
          </ToggleButton>
          <ToggleButton value="not_held">
            Not held {(notHeldTotal ?? 0).toLocaleString()}
          </ToggleButton>
        </ToggleButtonGroup>
      )}
    </Box>
  );
}

/**
 * What the last hold or release did. Every refused row is listed with the
 * server's own message, so a bulk action never reads as "all held" when some
 * were not.
 */
export function HoldResultAlert({
  result,
  onDismiss,
}: {
  result: HoldActionResult;
  onDismiss?: () => void;
}) {
  const refused = result.refused;
  return (
    <Alert
      severity={refused.length > 0 ? 'warning' : 'success'}
      onClose={onDismiss}
      sx={{ mb: 1.5 }}
    >
      {holdResultSummary(result)}
      {refused.length > 0 && (
        // Every refused row is listed, however many; a bulk hold over a large
        // selection scrolls rather than pushing the table off the screen.
        <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5, maxHeight: 240, overflowY: 'auto' }}>
          {refused.map(r => (
            <li key={r.id}>{r.message}</li>
          ))}
        </Box>
      )}
    </Alert>
  );
}

/**
 * After a commit: which rows it left out because they are held, and which
 * children it could not write because a parent is held (GDN-4065). Stays until
 * dismissed or the next commit starts; the snackbar alone would be gone before
 * anyone read a list.
 */
export function CommitHeldAlert({
  held,
  onDismiss,
}: {
  held: CommitHeldResult;
  onDismiss?: () => void;
}) {
  return (
    <Alert severity="info" onClose={onDismiss} sx={{ mb: 1.5 }}>
      {held.count > 0 && (
        <CommitHeldList
          heading={commitHeldHeading('held', held.count)}
          rows={held.rows}
          count={held.count}
        />
      )}
      {held.dependencyCount > 0 && (
        <CommitHeldList
          heading={commitHeldHeading('blocked', held.dependencyCount)}
          rows={held.dependencyRows}
          count={held.dependencyCount}
        />
      )}
    </Alert>
  );
}

function CommitHeldList({
  heading,
  rows,
  count,
}: {
  heading: string;
  rows: CommitHeldRow[];
  count: number;
}) {
  const more = commitHeldMore(rows.length, count);
  return (
    <Box sx={{ '& + &': { mt: 1 } }}>
      {heading}
      <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5, maxHeight: 200, overflowY: 'auto' }}>
        {rows.map(r => (
          <li key={r.import_resource_id}>
            {r.resource_type} · {r.source_key}
          </li>
        ))}
      </Box>
      {more && (
        <Typography variant="caption" color="text.secondary" component="p">
          {more}
        </Typography>
      )}
    </Box>
  );
}
